import React, { useMemo, useState } from 'react';
import { NumberedPagination, SortableTableHead } from '@kioskgaming/ui';
import { RefreshCw, Coins, Filter } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import { LoadingSpinner } from '../../ui/Loading';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '../../ui/Table';
import { AgentBalanceItem, AgentBalancesFilters } from '../../../types';
import {
  useAgentBalances,
  useSyncAgentBalances,
  useGameProviders,
} from '../../../hooks/useApi';
import { formatCurrency, cn } from '../../../utils';
import { useFormatDate } from '../../../hooks/useFormatDate';
import { getTableSortColumns } from '../../../config/tableSortConfig';
import { resolveHeaderSort, useTableSort } from '../../../hooks/useTableSort';
import GameProviderStatusBadge from './GameProviderStatusBadge';

const DEFAULT_FILTERS: AgentBalancesFilters = {
  page: 1,
  limit: 100,
};

const AgentBalancesTab: React.FC = () => {
  const fmt = useFormatDate();
  const [filters, setFilters] = useState<AgentBalancesFilters>(DEFAULT_FILTERS);

  const tableSort = useTableSort({
    columns: getTableSortColumns('agentBalances'),
    paramPrefix: 'ab',
    onChange: () => setFilters((prev) => ({ ...prev, page: 1 })),
  });

  const queryFilters = useMemo(
    () => ({
      ...filters,
      ...tableSort.sortParams,
    }),
    [filters, tableSort.sortParams],
  );

  const { data: balancesData, isLoading, error, refetch, isFetching } =
    useAgentBalances(queryFilters);
  const syncMutation = useSyncAgentBalances();
  const { data: providersData } = useGameProviders();

  const appliedSort = useMemo(
    () =>
      balancesData?.data?.sortBy
        ? {
            sortBy: balancesData.data.sortBy ?? null,
            sortOrder: balancesData.data.sortOrder ?? null,
          }
        : null,
    [balancesData],
  );

  const headerSort = resolveHeaderSort(tableSort, appliedSort);
  const isTableBusy = isLoading || isFetching;

  const providers = useMemo(
    () => providersData?.data?.items || [],
    [providersData]
  );
  const items: AgentBalanceItem[] = useMemo(
    () => balancesData?.data?.items || [],
    [balancesData]
  );
  const total = balancesData?.data?.total ?? 0;
  const page = balancesData?.data?.page ?? 1;
  const limit = balancesData?.data?.limit ?? 100;
  const totalPages = Math.ceil(total / limit) || 1;

  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleGameProviderFilter = (gameProviderId: string) => {
    setFilters((prev) => ({
      ...prev,
      gameProviderId: gameProviderId || undefined,
      page: 1,
    }));
  };

  const handleSync = () => {
    syncMutation.mutate(undefined, {
      onSuccess: () => refetch(),
    });
  };

  return (
    <Card className="overflow-hidden border-border/80 shadow-sm">
      <CardHeader className="border-b bg-muted/30 pb-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="flex items-center text-lg font-semibold tracking-tight">
              <Coins className="mr-2 h-5 w-5 text-muted-foreground" />
              Agent balance by game
              <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-sm font-normal tabular-nums text-muted-foreground">
                {total}
              </span>
            </CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Data is synced every 10 minutes from game APIs.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isLoading}
              className="border-border/80"
            >
              <RefreshCw
                className={cn('mr-2 h-4 w-4', isLoading && 'animate-spin')}
              />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={handleSync}
              disabled={syncMutation.isLoading}
            >
              <RefreshCw
                className={cn('mr-2 h-4 w-4', syncMutation.isLoading && 'animate-spin')}
              />
              Sync now
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="mb-5 flex flex-wrap items-end gap-2">
          <label htmlFor="agent-bal-game-filter" className="flex flex-wrap items-center gap-2">
            <Filter className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">Game</span>
          </label>
          <select
            id="agent-bal-game-filter"
            className="h-9 min-w-[12rem] rounded-md border border-input bg-background px-3 text-sm shadow-sm"
            value={filters.gameProviderId ?? ''}
            onChange={(e) => handleGameProviderFilter(e.target.value)}
          >
            <option value="">All games</option>
            {providers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.code})
              </option>
            ))}
          </select>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-destructive/10 text-destructive px-4 py-3 text-sm">
            Failed to load agent balances.{' '}
            <button
              type="button"
              onClick={() => refetch()}
              className="underline font-medium"
            >
              Try again
            </button>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border border-border/60 bg-card shadow-sm">
              <Table className="min-w-[720px]" aria-busy={isTableBusy || undefined}>
                <TableHeader className="border-b border-border/60 bg-muted/40 [&_tr]:border-border/60">
                  <TableRow className="border-0 hover:bg-transparent">
                    <SortableTableHead
                      sortable
                      label="Game"
                      sortKey="providerName"
                      activeKey={headerSort.activeKey}
                      direction={headerSort.direction}
                      onSort={tableSort.toggleSort}
                      disabled={isTableBusy}
                      className="px-3 py-2.5 h-auto text-xs font-medium uppercase tracking-wide text-muted-foreground"
                    />
                    <SortableTableHead
                      sortable
                      label="Code"
                      sortKey="providerCode"
                      activeKey={headerSort.activeKey}
                      direction={headerSort.direction}
                      onSort={tableSort.toggleSort}
                      disabled={isTableBusy}
                      className="px-3 py-2.5 h-auto text-xs font-medium uppercase tracking-wide text-muted-foreground"
                    />
                    <SortableTableHead
                      sortable
                      label="Status"
                      sortKey="status"
                      activeKey={headerSort.activeKey}
                      direction={headerSort.direction}
                      onSort={tableSort.toggleSort}
                      disabled={isTableBusy}
                      className="px-3 py-2.5 h-auto text-xs font-medium uppercase tracking-wide text-muted-foreground"
                    />
                    <SortableTableHead
                      sortable
                      label="Currency"
                      sortKey="currency"
                      activeKey={headerSort.activeKey}
                      direction={headerSort.direction}
                      onSort={tableSort.toggleSort}
                      disabled={isTableBusy}
                      className="px-3 py-2.5 h-auto text-xs font-medium uppercase tracking-wide text-muted-foreground"
                    />
                    <SortableTableHead
                      sortable
                      label="Balance"
                      sortKey="balance"
                      activeKey={headerSort.activeKey}
                      direction={headerSort.direction}
                      onSort={tableSort.toggleSort}
                      disabled={isTableBusy}
                      align="right"
                      className="px-3 py-2.5 h-auto text-xs font-medium uppercase tracking-wide text-muted-foreground"
                    />
                    <SortableTableHead
                      sortable
                      label="Last synced"
                      sortKey="lastSyncedAt"
                      activeKey={headerSort.activeKey}
                      direction={headerSort.direction}
                      onSort={tableSort.toggleSort}
                      disabled={isTableBusy}
                      className="px-3 py-2.5 h-auto text-xs font-medium uppercase tracking-wide text-muted-foreground"
                    />
                  </TableRow>
                </TableHeader>
                <TableBody className={cn(isTableBusy && 'opacity-60 transition-opacity')}>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="px-3 py-12 text-center text-sm text-muted-foreground">
                        No agent balances found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((row) => (
                      <TableRow key={row.gameProviderId} className="border-border/40 hover:bg-muted/25">
                        <TableCell className="px-3 py-2.5 font-semibold text-foreground">
                          {row.providerName}
                        </TableCell>
                        <TableCell className="px-3 py-2.5">
                          <code className="rounded-md bg-muted/70 px-2 py-1 font-mono text-xs ring-1 ring-border/50">
                            {row.providerCode}
                          </code>
                        </TableCell>
                        <TableCell className="px-3 py-2.5">
                          <GameProviderStatusBadge status={row.status} />
                        </TableCell>
                        <TableCell className="px-3 py-2.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          {row.currency}
                        </TableCell>
                        <TableCell className="px-3 py-2.5 text-right font-mono text-sm tabular-nums">
                          {row.balance != null
                            ? formatCurrency(row.balance, row.currency)
                            : '—'}
                        </TableCell>
                        <TableCell className="px-3 py-2.5 text-sm text-muted-foreground tabular-nums">
                          {fmt(row.lastSyncedAt)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Page {page} of {totalPages} ({total} total)
                </p>
                <NumberedPagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  variant="admin"
                />
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AgentBalancesTab;
